import type { WebSocketSessionInfo } from './webSocketService';
import type { AppDispatch } from '../../data/store';
import type { TripLeg } from '../../data/models/trip-leg.model';
import {
  attestTripLeg,
  upsertProviderTrip,
  upsertProviderDriverAssigned,
  removeFromTripLegs,
  removeFromProviderTrips,
  setTripLegEvent,
  setTripLegExceptionEvent,
  setTripLegDiscussionEvent,
} from '../../data/trip.slice';
import { setOfferedTripLegEvent } from '../../data/tripPackageInventory.slice';
import { TripLegStatus } from '../../data/models/trip-leg-status.model';
enum TripLegEventType {
  DraftTripLegUpdated = 'DraftTripLegUpdated',
  OpenTripLegUpdated = 'OpenTripLegUpdated',
  OfferedTripLegUpdated = 'OfferedTripLegUpdated',
  AcceptedTripLegUpdated = 'AcceptedTripLegUpdated',
  OnSiteTripLegUpdated = 'OnSiteTripLegUpdated',
  OnBoardTripLegUpdated = 'OnBoardTripLegUpdated',
  CompletedTripLegUpdated = 'CompletedTripLegUpdated',
  TripLegAbandoned = 'TripLegAbandoned',
  TripLegAccepted = 'TripLegAccepted',
  TripLegArrived = 'TripLegArrived',
  TripLegBoarded = 'TripLegBoarded',
  TripLegCanceled = 'TripLegCanceled',
  TripLegCanceledLate = 'TripLegCanceledLate',
  TripLegCompleted = 'TripLegCompleted',
  TripLegConfirmed = 'TripLegConfirmed',
  TripLegDenied = 'TripLegDenied',
  TripLegDrafted = 'TripLegDrafted',
  TripLegDriverAssigned = 'TripLegDriverAssigned',
  TripLegDriverInTransit = 'TripLegDriverInTransit',
  TripLegHeld = 'TripLegHeld',
  TripLegInventoried = 'TripLegInventoried',
  TripLegManuallyAllocated = 'TripLegManuallyAllocated',
  TripLegNotLoaded = 'TripLegNotLoaded',
  TripLegOffered = 'TripLegOffered',
  TripLegOpened = 'TripLegOpened',
  TripLegRejected = 'TripLegRejected',
  TripLegAttested = 'TripLegAttested',
  TripLegException = 'TripLegException',
  TripLegExceptionCleared = 'TripLegExceptionCleared',
  TripLegUnderDiscussion = 'TripLegUnderDiscussion',
  TripLegUnderDiscussionCleared = 'TripLegUnderDiscussionCleared',
}

/**
 * Method for handling all TripLeg Events
 * @param sessionInfo The webSocketService sessionInfo object. Contains data copied over from the Redux store as needed
 * @param dispatch The Redux store dispatch function
 * @param detailType A string representing the type of event. Must be one of the TripLegEventType enum values
 * @param detail An object containing the event data, in this case a TripLeg
 */
const updateTripLeg = (
  sessionInfo: WebSocketSessionInfo,
  dispatch: AppDispatch,
  detailType: string,
  detail: TripLeg,
) => {
  switch (detailType) {
    case TripLegEventType.TripLegOpened:
    case TripLegEventType.TripLegOffered:
    case TripLegEventType.TripLegConfirmed:
    case TripLegEventType.TripLegAccepted:
    case TripLegEventType.TripLegDriverInTransit:
    case TripLegEventType.DraftTripLegUpdated:
    case TripLegEventType.AcceptedTripLegUpdated:
    case TripLegEventType.OpenTripLegUpdated:
      if (
        detail.transportationProviderID ===
        sessionInfo.loggedInTransportationProviderID
      ) {
        dispatch(
          // tripPackageInventorySlice.addPackage(detail as TripLegPackage),
          upsertProviderTrip(detail as TripLeg),
        );
      }
      break;
    case TripLegEventType.OfferedTripLegUpdated:
      if (
        detail.transportationProviderID ===
        sessionInfo.loggedInTransportationProviderID
      ) {
        dispatch(upsertProviderTrip(detail as TripLeg));
        dispatch(setOfferedTripLegEvent(true));
      }
      break;
    case TripLegEventType.TripLegArrived:
    case TripLegEventType.TripLegNotLoaded:
    case TripLegEventType.TripLegBoarded:
    case TripLegEventType.TripLegCanceledLate:
    case TripLegEventType.TripLegCompleted:
    case TripLegEventType.OnSiteTripLegUpdated:
    case TripLegEventType.OnBoardTripLegUpdated:
    case TripLegEventType.CompletedTripLegUpdated:
      // For these events, we don't want to upsert because there are manual entries
      // on attestation that won't sync in time for the event, so any other pages requiring
      // the status will need to handle their own data fetches.
      dispatch(setTripLegEvent(detail.status as TripLegStatus));
      break;
    case TripLegEventType.TripLegDriverAssigned:
      dispatch(
        upsertProviderDriverAssigned({
          tripLegReferenceID: detail.tripLegReferenceID,
          transportationProviderID: detail.transportationProviderID,
          driver: detail.driver,
        }),
      );
      break;
    case TripLegEventType.TripLegCanceled:
      // Canceled trip legs should be removed from the store
      dispatch(removeFromTripLegs(detail.tripLegReferenceID));
      dispatch(removeFromProviderTrips(detail.tripLegReferenceID));
      break;
    case TripLegEventType.TripLegAbandoned:
    case TripLegEventType.TripLegDenied:
    case TripLegEventType.TripLegDrafted:
    case TripLegEventType.TripLegHeld:
    case TripLegEventType.TripLegInventoried:
    case TripLegEventType.TripLegManuallyAllocated:
    case TripLegEventType.TripLegRejected:
      break;
    case TripLegEventType.TripLegAttested:
      dispatch(attestTripLeg(detail));
      break;
    case TripLegEventType.TripLegException:
    case TripLegEventType.TripLegExceptionCleared:
      if (
        detail.transportationProviderID ===
        sessionInfo.loggedInTransportationProviderID
      ) {
        dispatch(setTripLegExceptionEvent(true));
      }
      break;
    case TripLegEventType.TripLegUnderDiscussion:
    case TripLegEventType.TripLegUnderDiscussionCleared:
      if (
        detail.transportationProviderID ===
        sessionInfo.loggedInTransportationProviderID
      ) {
        dispatch(setTripLegDiscussionEvent(true));
      }
      break;
  }
  dispatch(setTripLegEvent(detail.status as TripLegStatus));
};

export { TripLegEventType, updateTripLeg };
